import { useEffect, useState } from 'react';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import ReplayIcon from '@mui/icons-material/Replay';
import { CloudDownload as CloudDownloadIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { SttTopControls } from '../SttTopControls';
import { useInteractionsFiles } from '../../lib/hooks';
import { SttButton } from '../all';
import { SttAttendanceTable } from '../SttAttendanceTable';
import AttendanceDialog from '../../pages/sc/activities/AttendanceDialog';
import RecoverAttendanceDialog from '../../pages/sc/activities/RecoverAttendanceDialog';
import type { Activity } from '../../types/models/Activity';
import { DownloadFileFormat } from '../../types/components';
import type { ActivityAttendance } from '../../types/models/ActivityAssistance';
import useActivitiesService from '../../services/ActivitiesService';

type DayColumn = {
    title: string;
    field: string;
    type: 'text';
};

type SttFormAttendanceProps = {
    activity: Activity;
};

export function SttFormAttendance({ activity }: SttFormAttendanceProps) {
    const { t } = useTranslation();
    const { downloadFile } = useInteractionsFiles();
    const [filters, setFilters] = useState({
        year: new Date().getFullYear(),
        month: new Date().getMonth() + 1,
        page: 1,
        rows: 10,
    });

    const [dayColumns, setDayColumns] = useState<DayColumn[]>([]);
    const [open, setOpen] = useState(false);
    const [selectedAttendance, setSelectedAttendance] =
        useState<ActivityAttendance | null>(null);
    const [selectedAttendanceDay, setSelectedAttendanceDay] = useState<
        string | null
    >(null);
    const [openRecoverDialog, setOpenRecoverDialog] = useState(false);

    const getDayColumnsByMonthAndYear = (year: number, month: number) => {
        const daysInMonth = new Date(year, month, 0).getDate();
        const columns: DayColumn[] = [];

        for (let i = 1; i <= daysInMonth; i++) {
            columns.push({
                title: i.toString(),
                field: i.toString(),
                type: 'text',
            });
        }

        setDayColumns(columns);
    };
    const { useGetActivityStudentAssistences } = useActivitiesService();

    const getActivityStudentsAssistencesQuery =
        useGetActivityStudentAssistences(activity.id, filters, {
            enabled: false,
        });

    useEffect(() => {
        getDayColumnsByMonthAndYear(filters.year, filters.month);
    }, [filters]);

    useEffect(() => {
        if (dayColumns && dayColumns.length > 0) {
            getActivityStudentsAssistencesQuery.refetch();
        }
    }, [dayColumns]);

    const handleCell = (row: ActivityAttendance, col: DayColumn) => {
        setOpen(true);
        setSelectedAttendance(row);
        setSelectedAttendanceDay(col.field);
    };

    function download(format: DownloadFileFormat) {
        if (format === 'pdf') {
            downloadFile(
                `/activities/${activity.id}/students/assistences.pdf`,
                'application/pdf',
                filters,
                `${activity.name}.${format}`,
            );
        }

        if (format === 'csv') {
            downloadFile(
                `/activities/${activity.id}/students/assistences.csv`,
                'text/csv',
                filters,
                `${activity.name}.${format}`,
            );
        }
    }
    return (
        <>
            <Paper>
                <Box
                    mb={2}
                    sx={{
                        '& .MuiTextField-root': {
                            minWidth: 200,
                        },
                    }}
                >
                    <SttTopControls
                        p={2}
                        fields={[
                            {
                                caption: t('generic.year'),
                                name: 'year',
                                type: 'year',
                                value: filters.year,
                                xs: 12,
                                sm: 6,
                                md: 5,
                                lg: 4,
                            } as any,
                            {
                                caption: t('generic.month'),
                                name: 'month',
                                type: 'month',
                                value: filters.month,
                                xs: 12,
                                sm: 6,
                                md: 5,
                                lg: 4,
                            } as any,
                        ]}
                        mainAction={{
                            type: 'component',
                            component: (
                                <SttButton
                                    caption={t('generic.recover')}
                                    icon={<ReplayIcon />}
                                    onClick={() => {
                                        setOpenRecoverDialog(true);
                                    }}
                                />
                            ),
                        }}
                        menu={[
                            {
                                caption: `${t('generic.download')} PDF`,
                                onClick: () => download('pdf'),
                            },
                            {
                                caption: `${t('generic.download')} CSV`,
                                onClick: () => download('csv'),
                            },
                        ]}
                        menuIcon={<CloudDownloadIcon />}
                        onChange={
                            (({
                                name,
                                value,
                            }: {
                                name: string;
                                value: string | number;
                            }) =>
                                setFilters({
                                    ...filters,
                                    [name]:
                                        typeof value === 'string'
                                            ? Number(value)
                                            : value,
                                })) as any
                        }
                        onFilter={() =>
                            getActivityStudentsAssistencesQuery?.refetch()
                        }
                    />
                </Box>
            </Paper>

            <Paper>
                <Box
                    pl={2}
                    pr={2}
                    pt={2}
                    display="flex"
                    justifyContent="flex-end"
                >
                    <Box>
                        <b>X</b> = {t('CAS.MISSED')} <b>R</b> ={' '}
                        {t('CAS.RECOVERABLE')} <b>Rc</b> = {t('CAS.RECOVERER')}{' '}
                        y <b>Rd</b> = {t('CAS.RECOVERED')}
                    </Box>
                </Box>
                <Box pl={2} pr={2}>
                    <SttAttendanceTable
                        columns={dayColumns}
                        data={
                            getActivityStudentsAssistencesQuery?.data
                                ? getActivityStudentsAssistencesQuery?.data
                                : { count: 0, pages: 0, rows: [] }
                        }
                        onClickCell={handleCell}
                        onChangePageCallback={(p: any) =>
                            setFilters({ ...filters, page: p })
                        }
                    />
                </Box>
            </Paper>

            <AttendanceDialog
                open={open}
                onClose={() => {
                    setOpen(false);
                    getActivityStudentsAssistencesQuery.refetch();
                }}
                attendance={selectedAttendance as any}
                attendanceDay={selectedAttendanceDay}
            />

            <RecoverAttendanceDialog
                open={openRecoverDialog}
                onClose={() => {
                    setOpenRecoverDialog(false);
                    getActivityStudentsAssistencesQuery.refetch();
                }}
                activityId={activity.id}
            />
        </>
    );
}
